/**
 * The ONE ordering gate.
 *
 * `GET /:orgId/ordering/capabilities` answers "does this org have ordering, at
 * which venues, from which catalogues". Both nav context builders
 * (`useNewNavCtx`, `useNavCtx`) read `hasOrdering` from this hook, and the
 * Ordering layout reads the venues and catalogues from it. The survey found
 * each nav ctx computing its own Square gate; there is deliberately no third
 * copy of this one.
 *
 * Fails closed: `false` while loading or on error, so the section appears once
 * confirmed rather than flashing in and being retracted (the same convention
 * as `hasSquare` and `dataProviders`).
 */
import mouseApi from '@/state/mouse/mouse.slice'
import type { OrderingCapabilities } from '@/state/mouse/mouse.types'

export interface OrderingGate {
  /** True only when Mouse has confirmed ordering is on for the org. */
  ordering: boolean
  catalogues: OrderingCapabilities['catalogues']
  venues: string[]
  isLoading: boolean
  isError: boolean
  /** The raw response, for callers that want everything Mouse said. */
  data: OrderingCapabilities | undefined
}

/** Pure half of the gate, so the fail-closed rule is testable without React. */
export function resolveOrderingGate(
  data: OrderingCapabilities | undefined,
  isLoading: boolean,
  isError: boolean
): Pick<OrderingGate, 'ordering' | 'catalogues' | 'venues'> {
  if (isLoading || isError || !data) {
    return { ordering: false, catalogues: [], venues: [] }
  }
  return {
    ordering: data.ordering === true,
    catalogues: Array.isArray(data.catalogues) ? data.catalogues : [],
    venues: Array.isArray(data.venues) ? data.venues : [],
  }
}

export function useOrderingCapabilities(orgId: string | undefined): OrderingGate {
  const { data, isLoading, isError } =
    mouseApi.useGetOrderingCapabilitiesQuery(orgId ?? '', {
      skip: !orgId,
    })
  const resolved = resolveOrderingGate(data, isLoading, isError)
  return { ...resolved, isLoading, isError, data }
}
